import {
  useId,
  useRef,
  useState,
  type DragEvent,
  type MutableRefObject,
  type Ref,
} from 'react'
import { File, UploadCloud, X } from 'lucide-react'
import { cn } from '../../lib/cn'
import { Button } from './Button'

export interface FileDropzoneProps {
  label: string
  value?: File | null
  onChange?: (file: File | null) => void
  /** Множественный выбор: файлы накапливаются, каждый удаляется отдельно. */
  multiple?: boolean
  /** Выбранные файлы в режиме multiple. */
  values?: File[]
  /** Обязателен при multiple — onChange в этом режиме не вызывается. */
  onFilesChange?: (files: File[]) => void
  accept?: string
  helperText?: string
  error?: string
  disabled?: boolean
  compact?: boolean
  inputRef?: Ref<HTMLInputElement>
  className?: string
}

/** Размер файла по-русски: десятичная запятая («8,9 МБ»). */
export const formatFileSize = (size: number) => {
  if (size < 1024) return `${size} Б`
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} КБ`
  return `${(size / (1024 * 1024)).toFixed(1).replace('.', ',')} МБ`
}

const matchesAccept = (file: File, accept?: string) => {
  if (!accept) return true
  return accept.split(',').some((rawRule) => {
    const rule = rawRule.trim().toLowerCase()
    if (!rule) return false
    if (rule.startsWith('.')) return file.name.toLowerCase().endsWith(rule)
    if (rule.endsWith('/*')) return file.type.toLowerCase().startsWith(rule.slice(0, -1))
    return file.type.toLowerCase() === rule
  })
}

export function FileDropzone({
  label,
  value,
  onChange,
  multiple = false,
  values,
  onFilesChange,
  accept,
  helperText,
  error,
  disabled,
  compact = false,
  inputRef,
  className,
}: FileDropzoneProps) {
  const generatedId = useId()
  const ownRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [acceptError, setAcceptError] = useState('')
  const visibleError = error || acceptError

  const setRef = (node: HTMLInputElement | null) => {
    ownRef.current = node
    if (typeof inputRef === 'function') inputRef(node)
    else if (inputRef) {
      ;(inputRef as MutableRefObject<HTMLInputElement | null>).current = node
    }
  }

  const selected = multiple ? values || [] : []

  const choose = (file: File | null) => {
    if (!file) {
      setAcceptError('')
      onChange?.(null)
      return
    }
    if (!matchesAccept(file, accept)) {
      setAcceptError('Формат файла не соответствует допустимым типам')
      return
    }
    setAcceptError('')
    onChange?.(file)
  }

  /** Файлы добавляются к уже выбранным: несколько заходов — одна партия. */
  const chooseMany = (incoming: File[]) => {
    if (!incoming.length) return
    const supported = incoming.filter((file) => matchesAccept(file, accept))
    setAcceptError(
      supported.length === incoming.length ? '' : 'Формат файла не соответствует допустимым типам',
    )
    if (!supported.length) return
    const known = new Set(selected.map((file) => `${file.name}:${file.size}:${file.lastModified}`))
    const added = supported.filter(
      (file) => !known.has(`${file.name}:${file.size}:${file.lastModified}`),
    )
    if (added.length) onFilesChange?.([...selected, ...added])
  }

  const pick = (files: File[]) => (multiple ? chooseMany(files) : choose(files[0] || null))

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    if (disabled) return
    pick(Array.from(event.dataTransfer.files || []))
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      <label
        htmlFor={generatedId}
        className="block text-sm font-medium text-gray-700 dark:text-zinc-200"
      >
        {label}
      </label>
      <input
        ref={setRef}
        id={generatedId}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        tabIndex={-1}
        className="hidden"
        onChange={(event) => {
          pick(Array.from(event.target.files || []))
          if (multiple && ownRef.current) ownRef.current.value = ''
        }}
      />
      <div
        onDragEnter={(event) => {
          event.preventDefault()
          if (!disabled) setDragging(true)
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          'rounded-lg border border-dashed transition-colors',
          compact ? 'p-3' : 'p-5',
          dragging
            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30'
            : 'border-gray-300 bg-gray-50/70 dark:border-zinc-700 dark:bg-zinc-800/40',
          visibleError && 'border-red-400 bg-red-50/50 dark:border-red-800 dark:bg-red-950/20',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        {multiple && selected.length ? (
          <div className="space-y-2">
            {selected.map((file, index) => (
              <div key={`${file.name}-${file.size}-${index}`} className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-300">
                  <File className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-zinc-100">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-zinc-400">
                    {formatFileSize(file.size)}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={disabled}
                  aria-label={`Удалить файл ${file.name}`}
                  title="Удалить файл"
                  onClick={() =>
                    onFilesChange?.(selected.filter((_, position) => position !== index))
                  }
                  icon={<X className="h-4 w-4" />}
                />
              </div>
            ))}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled}
              onClick={() => ownRef.current?.click()}
              icon={<UploadCloud className="h-4 w-4" />}
            >
              Добавить ещё файлы
            </Button>
          </div>
        ) : value ? (
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-300">
              <File className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-gray-900 dark:text-zinc-100">
                {value.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-zinc-400">
                {formatFileSize(value.size)}
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              aria-label={`Удалить файл ${value.name}`}
              title="Удалить файл"
              onClick={() => {
                if (ownRef.current) ownRef.current.value = ''
                choose(null)
              }}
              icon={<X className="h-4 w-4" />}
            />
          </div>
        ) : (
          <button
            type="button"
            disabled={disabled}
            aria-label={multiple ? 'Выбрать файлы' : 'Выбрать файл'}
            onClick={() => ownRef.current?.click()}
            className="flex w-full flex-col items-center justify-center text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <UploadCloud className="mb-2 h-7 w-7 text-indigo-500" />
            <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
              {multiple ? 'Выбрать файлы' : 'Выбрать файл'}
            </span>
            <span className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400">
              {multiple ? 'или перетащить их сюда' : 'или перетащить его сюда'}
            </span>
          </button>
        )}
      </div>
      {helperText && !visibleError && (
        <p className="text-xs text-gray-500 dark:text-zinc-400">{helperText}</p>
      )}
      {visibleError && (
        <p role="alert" className="text-xs text-red-600 dark:text-red-400">{visibleError}</p>
      )}
    </div>
  )
}

export default FileDropzone
